<!--
  Inline widok importu CSV — zastępuje listę pojazdów na czas importu.
  Parsuje CSV po stronie klienta (papaparse), pokazuje podgląd wierszy i wymusza
  jawny wybór grup kontaktów (tak samo jak krok 2 kreatora pojedynczej tablicy).
-->
<template>
  <section class="wanted-csv">
    <header class="wanted-csv__head">
      <h3 class="wanted-csv__title">{{ title }}</h3>
      <p class="wanted-csv__hint">{{ t('wanted-csv-hint') }}</p>
    </header>

    <div class="wanted-csv__body">
      <!-- ============ Sekcja 1: plik CSV + podgląd wierszy ============ -->
      <section class="wcs-section">
        <h4 class="wcs-section__title">
          <span class="wcs-section__index">1</span>
          {{ t('wizard-step1') }}
        </h4>
        <input
          ref="fileInput"
          type="file"
          accept=".csv"
          class="form-control"
          @change="handleFileChange"
        />
        <div v-if="uploadedFileName" class="file-name">
          {{ uploadedFileName }}
          <button type="button" class="close-button file-remove" @click="reset">×</button>
        </div>

        <div v-if="rows.length > 0" class="preview">
          <p class="rows-count">{{ t('wanted-csv-rows-ready', { n: rows.length }) }}</p>
          <table class="preview-table">
            <thead>
              <tr>
                <th>#</th>
                <th>{{ t('short-plate-no') }}</th>
                <th>{{ t('vehicle-description') }}</th>
                <th>{{ t('action') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, idx) in rows" :key="idx">
                <td>{{ idx + 1 }}</td>
                <td class="plate-cell">{{ row.licensePlate }}</td>
                <td>{{ row.description }}</td>
                <td>
                  <button type="button" class="button-delete button-compact" @click="removeRow(idx)">
                    {{ t('delete') }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- ============ Sekcja 2: wybór grup odbiorców ============ -->
      <section class="wcs-section">
        <h4 class="wcs-section__title">
          <span class="wcs-section__index">2</span>
          {{ t('wizard-step2') }}
        </h4>
        <p class="step-hint">{{ t('wizard-step2-hint') }}</p>

        <input
          v-model="groupSearch"
          type="text"
          class="form-control search-input"
          :placeholder="t('search-group-placeholder')"
          autocomplete="off"
        />

        <ul v-if="filteredGroups.length > 0" class="group-list">
          <li
            v-for="group in filteredGroups"
            :key="group.id"
            class="group-item"
            :class="{ 'group-item--selected': selectedSet.has(group.id) }"
            @click="toggleGroup(group.id)"
          >
            <label class="group-item__label" @click.stop>
              <input type="checkbox" :checked="selectedSet.has(group.id)" @change="toggleGroup(group.id)" />
              <span class="group-item__name">{{ group.name }}</span>
            </label>
            <span class="group-item__counts">
              <span class="count-pill">{{ group.contactsSummary?.count ?? 0 }} {{ t('recipients-short') }}</span>
              <span v-if="(group.contactsSummary?.emails ?? 0) > 0" class="count-pill count-pill--email">
                {{ group.contactsSummary.emails }} e-mail
              </span>
              <span v-if="(group.contactsSummary?.sms ?? 0) > 0" class="count-pill count-pill--sms">
                {{ group.contactsSummary.sms }} SMS
              </span>
              <span v-if="(group.contactsSummary?.mms ?? 0) > 0" class="count-pill count-pill--mms">
                {{ group.contactsSummary.mms }} MMS
              </span>
            </span>
          </li>
        </ul>
        <p v-else class="empty-state">
          {{ groupSearch ? t('no-matches') : t('no-contact-groups') }}
        </p>
      </section>

      <p v-if="error" class="step-error">{{ error }}</p>
    </div>

    <footer class="wanted-csv__foot">
      <button type="button" class="button-add-user" @click="cancel">{{ t('cancel') }}</button>
      <span class="spacer"></span>
      <button
        type="button"
        class="button-save"
        :disabled="!canSubmit || submitting"
        @click="submit"
      >
        {{ submitting ? '...' : t('add-vehicles-from-csv') }}
      </button>
    </footer>
  </section>
</template>

<script setup>
import { computed, getCurrentInstance, ref } from 'vue';
import Papa from 'papaparse';

const props = defineProps({
  listType: { type: String, default: 'black' },
  // Wszystkie dostępne grupy kontaktów — operator wybiera z nich w sekcji 2.
  availableGroups: { type: Array, default: () => [] },
  submitting: { type: Boolean, default: false },
});

const emit = defineEmits(['submit', 'cancel']);

const t = getCurrentInstance()?.appContext.config.globalProperties.t ?? (k => k);

const rows = ref([]);
const uploadedFileName = ref('');
const error = ref(null);
const fileInput = ref(null);

// Wybór grup — lokalny, startuje pusty (jawna decyzja przy każdym imporcie).
const selected = ref([]);
const groupSearch = ref('');
const selectedSet = computed(() => new Set(selected.value));

const title = computed(() => {
  if (props.listType === 'white') return `${t('import-csv')} → ${t('white-list')}`;
  return `${t('import-csv')} → ${t('black-list')}`;
});

const filteredGroups = computed(() => {
  const phrase = groupSearch.value.trim().toLowerCase();
  if (!phrase) return props.availableGroups;
  return props.availableGroups.filter(g => String(g.name ?? '').toLowerCase().includes(phrase));
});

const canSubmit = computed(() => rows.value.length > 0 && selected.value.length > 0);

function handleFileChange(event) {
  const file = event.target.files?.[0];
  if (!file) return;
  uploadedFileName.value = file.name;
  error.value = null;
  Papa.parse(file, {
    header: false,
    skipEmptyLines: true,
    delimiter: ';',
    complete(results) {
      rows.value = (results.data || [])
        .map(row => ({
          licensePlate: String(row[0] ?? '').replace(/\s+/g, '').toUpperCase(),
          description: String(row[1] ?? '').trim(),
        }))
        .filter(r => r.licensePlate);
      if (rows.value.length === 0) {
        error.value = t('wanted-csv-empty');
      }
    },
    error(err) {
      console.error('CSV parse error:', err);
      error.value = t('wanted-csv-parse-error');
    },
  });
}

function removeRow(idx) {
  rows.value.splice(idx, 1);
}

function reset() {
  rows.value = [];
  uploadedFileName.value = '';
  error.value = null;
  if (fileInput.value) {
    fileInput.value.value = '';
  }
}

function toggleGroup(id) {
  const num = Number(id);
  if (selected.value.includes(num)) {
    selected.value = selected.value.filter(x => x !== num);
  } else {
    selected.value = [...selected.value, num];
  }
}

function submit() {
  if (rows.value.length === 0) {
    error.value = t('wanted-csv-empty');
    return;
  }
  if (selected.value.length === 0) {
    error.value = t('wanted-group-required');
    return;
  }
  emit('submit', { rows: [...rows.value], contactGroupIds: [...selected.value] });
}

function cancel() {
  emit('cancel');
}
</script>

<style scoped>
.wanted-csv {
  background-color: var(--surface-primary);
  border: 1px solid var(--border-default);
  border-radius: var(--panel-radius);
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.wanted-csv__head {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--border-subtle);
  background-color: var(--surface-secondary);
}
.wanted-csv__title {
  margin: 0 0 0.5rem;
  font-size: 1.15rem;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: 0.01em;
}
.wanted-csv__hint {
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.92rem;
  line-height: 1.5;
}

.wanted-csv__body {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
}

/* Sekcje numerowane — ujednolicony rytm z krokami wizarda. */
.wcs-section {
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
}
.wcs-section__title {
  margin: 0;
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-primary);
  letter-spacing: 0.01em;
}
.wcs-section__index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.4rem;
  height: 1.4rem;
  border-radius: 50%;
  background-color: var(--text-primary);
  color: var(--text-inverse);
  font-size: 0.78rem;
  font-weight: 700;
}

.wanted-csv__body :deep(input[type="file"]) {
  padding: 0.55rem 0.75rem;
  border: 2px dashed var(--accent-blue-border);
  background-color: var(--accent-blue-soft);
  border-radius: var(--control-radius);
  color: var(--text-primary);
  cursor: pointer;
}
.wanted-csv__body :deep(input[type="file"]:hover) {
  border-color: var(--accent-blue);
  background-color: var(--accent-blue-soft);
}

.file-name {
  color: var(--text-primary);
  font-size: 0.9rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.75rem;
  background-color: var(--status-ok-soft);
  border: 1px solid var(--status-ok-border);
  border-radius: var(--control-radius);
  font-weight: var(--font-weight-medium);
  align-self: flex-start;
}
.file-remove {
  font-size: 1rem !important;
  color: var(--status-error-strong) !important;
}

.preview { display: flex; flex-direction: column; gap: 0.6rem; }
.rows-count {
  margin: 0;
  color: var(--text-primary);
  font-weight: var(--font-weight-medium);
  font-size: 0.92rem;
}
.preview-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background-color: var(--surface-primary);
  border: 1px solid var(--border-default);
  border-radius: var(--control-radius);
  overflow: hidden;
}
.preview-table th,
.preview-table td {
  padding: 0.55rem 0.85rem;
  border-bottom: 1px solid var(--border-subtle);
  text-align: left;
  vertical-align: middle;
}
.preview-table tbody tr:last-child td { border-bottom: 0; }
.preview-table tbody tr:nth-child(even) { background-color: var(--surface-secondary); }
.preview-table thead th {
  background-color: var(--surface-secondary);
  color: var(--text-secondary);
  font-size: 0.74rem;
  font-weight: var(--font-weight-medium);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 2px solid var(--text-primary);
}
.preview-table th:first-child,
.preview-table td:first-child {
  width: 3rem;
  text-align: right;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
}
.preview-table th:last-child,
.preview-table td:last-child {
  width: 6rem;
  text-align: right;
}

.plate-cell {
  display: inline-block;
  font-family: ui-monospace, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace;
  font-weight: 700;
  font-size: 0.9rem;
  letter-spacing: 0.06em;
  padding: 0.15rem 0.5rem;
  border: 1.5px solid var(--text-primary);
  border-radius: var(--control-radius);
  background: var(--surface-primary);
  color: var(--text-primary);
}

/* ---- sekcja 2: lista grup kontaktów (1:1 wizard step 2) -------------- */
.step-hint {
  color: var(--text-secondary);
  margin: 0;
  font-size: 0.92rem;
}
.search-input {
  margin: 0;
  border-radius: var(--control-radius);
}
.group-list {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid var(--border-default);
  border-radius: var(--control-radius);
  background-color: var(--surface-primary);
  max-height: 22rem;
  overflow-y: auto;
}
.group-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.7rem 0.9rem;
  border-bottom: 1px solid var(--border-subtle);
  cursor: pointer;
  color: var(--text-primary);
  transition: background-color 0.12s ease;
  position: relative;
}
.group-item:last-child { border-bottom: 0; }
.group-item:hover { background-color: var(--surface-secondary); }
.group-item--selected {
  background-color: var(--accent-blue-soft);
  box-shadow: inset 3px 0 0 var(--accent-blue);
}
.group-item--selected:hover { background-color: var(--accent-blue-soft); }
.group-item__label {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex: 1;
  cursor: pointer;
  min-width: 0;
}
.group-item__label input[type="checkbox"] {
  width: 1rem;
  height: 1rem;
  accent-color: var(--accent-blue);
  cursor: pointer;
}
.group-item__name {
  font-weight: var(--font-weight-medium);
  overflow: hidden;
  text-overflow: ellipsis;
}
.group-item__counts {
  display: inline-flex;
  gap: 0.3rem;
  font-size: 0.78rem;
  font-variant-numeric: tabular-nums;
  flex-wrap: wrap;
  justify-content: flex-end;
}
.count-pill {
  background-color: var(--surface-secondary);
  border: 1px solid var(--border-default);
  border-radius: 999px;
  padding: 0.1rem 0.55rem;
  color: var(--text-secondary);
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
}
.count-pill--email { color: var(--accent-blue); background-color: var(--accent-blue-soft); border-color: var(--accent-blue-border); }
.count-pill--sms   { color: var(--status-ok); background-color: var(--status-ok-soft); border-color: var(--status-ok-border); }
.count-pill--mms   { color: var(--status-warn-strong); background-color: var(--status-warn-soft); border-color: var(--status-warn-border); }

.empty-state {
  padding: 2rem 1rem;
  text-align: center;
  color: var(--text-secondary);
  background-color: var(--surface-secondary);
  border: 1px dashed var(--border-default);
  border-radius: var(--control-radius);
}

.step-error {
  color: var(--status-error-strong);
  margin: 0;
  padding: 0.55rem 0.85rem;
  background-color: var(--status-error-soft);
  border: 1px solid var(--status-error-border);
  border-radius: var(--control-radius);
  font-size: 0.9rem;
  font-weight: var(--font-weight-medium);
}

.wanted-csv__foot {
  display: flex;
  align-items: center;
  padding: 0.85rem 1.25rem;
  border-top: 1px solid var(--border-subtle);
  gap: 0.55rem;
  background-color: var(--surface-secondary);
}
.spacer { flex: 1; }

.button-save {
  border-radius: 6px !important;
  padding: 0.45rem 0.95rem;
}
</style>
